import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Loader2, Star, CheckCircle2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SCORE_LABELS = {
  1: { emoji: '😡', text: 'Meget utilfreds', color: 'bg-red-500 hover:bg-red-600' },
  2: { emoji: '😕', text: 'Utilfreds', color: 'bg-orange-500 hover:bg-orange-600' },
  3: { emoji: '🙂', text: 'Tilfreds', color: 'bg-lime-500 hover:bg-lime-600' },
  4: { emoji: '😃', text: 'Meget tilfreds', color: 'bg-green-600 hover:bg-green-700' }
};

// Star rating component
const StarRating = ({ value, onChange, label }) => {
  const [hover, setHover] = useState(0);
  
  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-slate-700">{label}</label>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => onChange(star)}
            onMouseEnter={() => setHover(star)}
            onMouseLeave={() => setHover(0)}
            className="p-1 transition-transform hover:scale-110"
          >
            <Star
              className={`h-8 w-8 ${
                star <= (hover || value)
                  ? 'fill-yellow-400 text-yellow-400'
                  : 'text-slate-300'
              }`}
            />
          </button>
        ))}
      </div>
    </div>
  );
};

const FeedbackPage = () => {
  const { token } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const pendingScoreParam = searchParams.get('pending_score');
  const pendingScore = pendingScoreParam ? parseInt(pendingScoreParam, 10) : null;
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [survey, setSurvey] = useState(null);
  const [confirming, setConfirming] = useState(false);
  
  // Form state
  const [sellerRating, setSellerRating] = useState(0);
  const [installerRating, setInstallerRating] = useState(0);
  const [comment, setComment] = useState('');
  const [wantsContact, setWantsContact] = useState(false);
  
  const confirmRating = async (score) => {
    setConfirming(true);
    try {
      await axios.post(`${API}/satisfaction/rate/${token}/confirm`, { score });
      // Refresh survey state and strip pending_score from URL
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('pending_score');
      setSearchParams(newParams, { replace: true });
      await loadSurvey();
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Kunne ikke gemme din vurdering. Prøv igen.';
      setError(detail);
    } finally {
      setConfirming(false);
    }
  };
  
  // Load survey on mount
  useEffect(() => {
    loadSurvey();
  }, [token]);
  
  const loadSurvey = async () => {
    try {
      const res = await axios.get(`${API}/satisfaction/survey/${token}`);
      setSurvey(res.data);
      
      // Pre-fill if already submitted
      if (res.data.seller_rating) setSellerRating(res.data.seller_rating);
      if (res.data.installer_rating) setInstallerRating(res.data.installer_rating);
      if (res.data.comment) setComment(res.data.comment);
      if (res.data.wants_contact) setWantsContact(res.data.wants_contact);
      if (res.data.detailed_submitted_at) setSubmitted(true);
    } catch (err) {
      setError('Kunne ikke indlæse spørgeskemaet');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await axios.post(`${API}/satisfaction/survey/${token}/detailed`, {
        seller_rating: sellerRating || null,
        installer_rating: installerRating || null,
        comment: comment.trim(),
        wants_contact: (survey?.overall_score && survey.overall_score <= 2) ? wantsContact : false
      });
      setSubmitted(true);
    } catch (err) {
      setError('Kunne ikke gemme din feedback');
    } finally {
      setSubmitting(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }
  
  if (error && !survey) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <p className="text-red-500">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-0 shadow-lg">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="h-8 w-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">
              Tak for din feedback!
            </h1>
            <p className="text-slate-500">
              Din mening hjælper os med at blive bedre.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  const scoreEmoji = {
    1: '😡',
    2: '😕',
    3: '🙂',
    4: '😃'
  };
  
  const scoreText = {
    1: 'Meget utilfreds',
    2: 'Utilfreds',
    3: 'Tilfreds',
    4: 'Meget tilfreds'
  };

  const isDissatisfied = survey?.overall_score && survey.overall_score <= 2;
  
  // If user arrived via email link AND hasn't confirmed yet, show big confirmation screen.
  // This prevents email security scanners from accidentally registering a rating.
  if (pendingScore && pendingScore >= 1 && pendingScore <= 4 && !survey?.overall_score) {
    const label = SCORE_LABELS[pendingScore];
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-0 shadow-lg">
          <CardContent className="pt-8 pb-8 text-center">
            <img 
              src="/gardinbussen-logo.png" 
              alt="Gardinbussen" 
              className="h-12 mx-auto mb-6"
            />
            <h1 className="text-xl font-semibold text-slate-900 mb-2">
              Bekræft din vurdering
            </h1>
            <p className="text-sm text-slate-500 mb-6">
              Klik på knappen nedenfor for at sende din vurdering til Gardinbussen.
            </p>
            <div className="bg-slate-50 rounded-xl p-6 mb-6 border border-slate-200">
              <div className="text-6xl mb-3">{label.emoji}</div>
              <div className="text-lg font-semibold text-slate-900">{label.text}</div>
            </div>
            <Button
              onClick={() => confirmRating(pendingScore)}
              disabled={confirming}
              className={`w-full ${label.color} text-white py-6 text-lg font-semibold`}
              data-testid="confirm-rating-button"
            >
              {confirming ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <>Bekræft min vurdering</>
              )}
            </Button>
            {error && (
              <p className="text-sm text-red-600 mt-3">{error}</p>
            )}
            <p className="text-xs text-slate-400 mt-6">
              Hvis du har klikket ved en fejl, så luk blot dette vindue.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-md mx-auto space-y-6">
        {/* Header */}
        <div className="text-center">
          <img 
            src="/gardinbussen-logo.png" 
            alt="Gardinbussen" 
            className="h-12 mx-auto mb-4"
          />
          <h1 className="text-2xl font-bold text-slate-900">
            Tak for dit svar!
          </h1>
          {survey?.overall_score && (
            <div className="mt-3 inline-flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm">
              <span className="text-2xl">{scoreEmoji[survey.overall_score]}</span>
              <span className="text-slate-600">{scoreText[survey.overall_score]}</span>
            </div>
          )}
        </div>
        
        {/* Detailed feedback form */}
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="text-lg">Vil du uddybe?</CardTitle>
            <p className="text-sm text-slate-500">
              Valgfrit – men det hjælper os med at blive bedre
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Seller rating */}
              <StarRating
                label="Hvordan var sælgeren?"
                value={sellerRating}
                onChange={setSellerRating}
              />
              
              {/* Installer rating */}
              <StarRating
                label="Hvordan var montagen?"
                value={installerRating}
                onChange={setInstallerRating}
              />
              
              {/* Comment */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Kommentar (valgfrit)
                </label>
                <Textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Fortæl os gerne mere om din oplevelse..."
                  rows={4}
                  className="resize-none"
                />
              </div>
              
              {/* Contact me checkbox - only for dissatisfied customers */}
              {isDissatisfied && (
                <div
                  className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg"
                  data-testid="wants-contact-section"
                >
                  <Checkbox
                    id="wants-contact"
                    checked={wantsContact}
                    onCheckedChange={(checked) => setWantsContact(!!checked)}
                    className="mt-0.5"
                    data-testid="wants-contact-checkbox"
                  />
                  <div className="space-y-1">
                    <label
                      htmlFor="wants-contact"
                      className="text-sm font-medium text-slate-900 cursor-pointer block"
                    >
                      Jeg vil gerne kontaktes af Gardinbussen
                    </label>
                    <p className="text-xs text-slate-600">
                      Sæt flueben hvis du ønsker at vi følger op telefonisk eller på mail.
                    </p>
                  </div>
                </div>
              )}
              
              {error && (
                <p className="text-sm text-red-500">{error}</p>
              )}
              
              <Button
                type="submit"
                disabled={submitting || (!sellerRating && !installerRating && !comment.trim())}
                className="w-full h-12 bg-slate-900 hover:bg-slate-800"
              >
                {submitting ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  'Send feedback'
                )}
              </Button>
              
              <p className="text-xs text-center text-slate-400">
                Ordre #{survey?.order_id}
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default FeedbackPage;
